// Port of packages/gat-attachments/assets/js/uploaders.js
import axios from "axios";
import axiosRetry from "axios-retry";

const axiosClient = axios.create({});
const retry_interval_constant = 1000;
axiosRetry(axiosClient, {
  retries: 3,
  retryDelay: (retryCount, error) => axiosRetry.exponentialDelay(retryCount, error, retry_interval_constant),
});

let inflightUploads = {};
const errorMessage = "Something went wrong with upload. Please try again.";

// One hook instance per uploader widget on the page, keyed by the upload ref
// (`data-phx-upload-ref` on that instance's file input) so an event coming back from a foreign
// entry (progress, presign, chunk upload) can be routed to the hook that owns it.
const hooksByRef = new Map();

const viewCanPush = () => {
  const view = window.liveSocket && window.liveSocket.main;

  return Boolean(view && view.isConnected());
};

const hookForEntry = entry => {
  const ref = entry.fileEl && entry.fileEl.getAttribute("data-phx-upload-ref");

  return ref ? hooksByRef.get(ref) : undefined;
};

// All entries are cached inside inflightUploads with information from initial
// entry submission where we have original upload_id.
// It will be called once again in case server connection is broken and restored
// S3 uploads are not broken on interrupted server's connection
const fetchInflightUpload = entry => {
  const {
    meta: { file_id, upload_id },
  } = entry;
  const uploadRef = entry.fileEl && entry.fileEl.getAttribute("data-phx-upload-ref");

  inflightUploads[file_id] = inflightUploads[file_id] || {
    abortController: new AbortController(),
    originalEntry: entry,
    chunks: {},
    uploadId: upload_id,
    uploadRef,
  };

  return [
    inflightUploads[file_id],
    () => {
      delete inflightUploads[file_id];
    },
  ];
};

const uploadChunk = async (chunk, inflightUpload) => {
  const { start, end, part_number: partNumber, url } = chunk;
  inflightUpload.chunks[partNumber] = inflightUpload.chunks[partNumber] || { partNumber, etag: null };
  const inFlightChunk = inflightUpload.chunks[partNumber];
  const {
    originalEntry: { file },
    chunks,
  } = inflightUpload;
  const blob = end ? file.slice(start, end) : file.slice(start);

  // We reuse original xhr that might still be in progress.
  // Backend won't also generate a new file record but reuse existing one
  return new Promise((resolve, reject) => {
    if (inFlightChunk.etag) {
      resolve({ etag: inFlightChunk.etag, partNumber });
    } else {
      let request =
        inFlightChunk.request ||
        axiosClient({
          method: "put",
          url,
          data: blob,
          signal: inflightUpload.abortController.signal,
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: function ({ loaded, total, progress: _progress, bytes, estimated, rate, upload = true }) {
            const progress = Math.floor(((start + loaded) * 100) / file.size);
            inflightUpload.handleProgress(progress);
          },
        });

      inFlightChunk.request = request;
      request
        .then(response => {
          const rawEtag = response.headers.etag || response.headers.ETag;

          if (!rawEtag) {
            reject(new Error("S3 exposed no ETag header; check the bucket CORS ExposeHeaders configuration."));
            return;
          }

          const etag = rawEtag.replaceAll('"', "");
          inFlightChunk.etag = etag;

          resolve({ etag, partNumber });
        })
        .catch(error => {
          // Drop the settled promise, otherwise a reconnect replay re-attaches to this rejection
          // and the chunk can never succeed again.
          if (inFlightChunk.request === request) {
            inFlightChunk.request = null;
          }

          reject(error);
        });
    }
  });
};

const uploadToS3 = async (entry, onViewError, pushEventTo) => {
  // We can only send the error once because immedietly after error,
  // connection channel and entry is removed but we may try to send the error
  // twice in case the request is successful but AWS returned other than 204 status
  let errorSent = false;
  let brokenLiveViewConnectionDuringUpload = false;
  const [inflightUpload, cleanupInflightUpload] = fetchInflightUpload(entry);
  const {
    originalEntry: {
      meta: { file_id, chunks, upload_id },
    },
  } = inflightUpload;
  const parts = [];

  onViewError(() => {
    console.info("Detected broken view, marking this as broken connection");
    brokenLiveViewConnectionDuringUpload = true;
  });

  entry.onCancel(() => {
    const record = inflightUploads[file_id];

    if (record) {
      record.abortController.abort();
      cleanupInflightUpload();
    }
  });

  // Avoids leaking previous handleProgress functions
  // We can look into this only when there's something wrong with server connection during upload
  delete inflightUpload.handleProgress;
  inflightUpload.handleProgress = progress => {
    if (!brokenLiveViewConnectionDuringUpload && progress < 100) {
      entry.progress(progress);
    }
  };

  try {
    for (const chunk of chunks) {
      const { etag, partNumber } = await uploadChunk(chunk, inflightUpload);

      parts.push({ etag, part_number: partNumber });
    }

    if (!brokenLiveViewConnectionDuringUpload) {
      // mark full progress only after all chunks were uploaded,
      // if it's not marked, file is not ready for validation
      pushEventTo(
        "uploader:completed-chunks-upload",
        {
          file_id,
          upload_id,
          parts,
        },
        reply => {
          if (reply && reply.error) {
            console.info("Server reply error", reply);
          } else {
            if (!brokenLiveViewConnectionDuringUpload) {
              entry.progress(100);
            }
          }

          cleanupInflightUpload();
        },
      );
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      return;
    }

    console.error("Uploader's error", error);

    if (!brokenLiveViewConnectionDuringUpload) {
      if (!errorSent) {
        entry.error(errorMessage);
        errorSent = true;
      }
      cleanupInflightUpload();
    }
  }
};

export default {
  S3(entries, onViewError) {
    entries.forEach(entry => {
      uploadToS3(entry, onViewError, (event, payload, cb) => {
        // Resolved lazily: a rejoin re-keys the hook, so a reference captured up front would be
        // stale by the time the chunks finish.
        const hook = hookForEntry(entry);

        if (!hook) {
          throw new Error("No uploader hook is mounted for this entry; cannot confirm the upload.");
        }

        hook.pushEventTo(hook.el, event, payload, cb);
      });
    });
  },

  registerRef() {
    const fileInput = this.el.querySelector('input[type="file"][data-phx-upload-ref]');
    const previousRef = this.uploadRef;

    this.uploadRef = fileInput && fileInput.getAttribute("data-phx-upload-ref");
    this.uploadName = fileInput && fileInput.name;

    if (previousRef && previousRef !== this.uploadRef) {
      if (hooksByRef.get(previousRef) === this) {
        hooksByRef.delete(previousRef);
      }

      Object.keys(inflightUploads).forEach(id => {
        if (inflightUploads[id].uploadRef === previousRef) {
          inflightUploads[id].uploadRef = this.uploadRef;
        }
      });
    }

    if (this.uploadRef) {
      hooksByRef.set(this.uploadRef, this);
    }

    return previousRef;
  },

  mounted() {
    this.selectionMadeWhileDisconnected = [];
    this.registerRef();

    this.rememberSelectionWhileDisconnected = event => {
      const input = event.target;

      if (input.type !== "file" || input.files.length === 0 || viewCanPush()) {
        return;
      }

      this.selectionMadeWhileDisconnected.push(...Array.from(input.files));
    };

    this.el.addEventListener("input", this.rememberSelectionWhileDisconnected, false);

    this.replaySelectionMadeWhileDisconnected();
  },

  reconnected() {
    const wrapperEl = this.el;
    const inputEls = Array.from(wrapperEl.querySelectorAll('input[type="file"][data-phx-upload-ref]'));

    if (inputEls.length !== 1) {
      throw new Error("Form is not correct and it's not possible to safely recover file uploads.");
    }

    // A rejoin mints a new upload ref, so re-key ourselves before looking anything up.
    this.registerRef();

    // inflightUploads is module-level and shared by every uploader instance on the page, so it may
    // hold files belonging to OTHER hooks too - only rebuild the ones that are ours.
    const files = Object.keys(inflightUploads)
      .filter(id => inflightUploads[id].uploadRef === this.uploadRef)
      .map(id => {
        const {
          originalEntry: { meta, file },
        } = inflightUploads[id];

        // File cannot have size = 0 (will be skipped) so we set bits to some dummy value
        // Only name matters on server side
        return new File(["0"], `file_id_${meta.file_id}`, { type: file.type });
      });

    this.uploadTo(wrapperEl, this.uploadName, files);
    this.replaySelectionMadeWhileDisconnected();
  },

  replaySelectionMadeWhileDisconnected() {
    if (this.selectionMadeWhileDisconnected.length === 0) {
      return;
    }

    const files = this.selectionMadeWhileDisconnected;
    this.selectionMadeWhileDisconnected = [];

    this.uploadTo(this.el, this.uploadName, files);
  },

  destroyed() {
    this.el.removeEventListener("input", this.rememberSelectionWhileDisconnected);

    if (this.uploadRef && hooksByRef.get(this.uploadRef) === this) {
      hooksByRef.delete(this.uploadRef);
    }
  },
};
